/**
 * Single EDGE measurement pipe.
 * Scene console, HUD overlay, and share card all read from here.
 */
import type { ProbeSampleLike } from "../scene/types";

export type EdgeSample = ProbeSampleLike;

export type EdgeHistoryItem = {
  rtt_ms: number | null;
  colo: string | null;
  created_at: string;
};

type Listener = (state: EdgeState) => void;

export type EdgeState = {
  samples: EdgeSample[];
  history: EdgeHistoryItem[];
};

const MAX_LIVE = 24;
const PROBE_INTERVAL_MS = 60_000;
const listeners = new Set<Listener>();
let samples: EdgeSample[] = [];
let history: EdgeHistoryItem[] = [];
let started = false;
let timer = 0;
let controller: AbortController | null = null;
let lifecycle = 0;

function emit(): void {
  const state = { samples: samples.slice(), history: history.slice() };
  for (const fn of listeners) fn(state);
}

export function subscribeEdge(fn: Listener): () => void {
  listeners.add(fn);
  fn({ samples: samples.slice(), history: history.slice() });
  return () => listeners.delete(fn);
}

export function getEdgeState(): EdgeState {
  return { samples: samples.slice(), history: history.slice() };
}

export function setEdgeHistory(items: EdgeHistoryItem[]): void {
  history = items.slice(-40);
  emit();
}

export function pushEdgeSamples(next: EdgeSample[]): void {
  if (!next.length) return;
  samples = next.slice(-MAX_LIVE);
  emit();
}

async function oneProbe(signal: AbortSignal): Promise<EdgeSample> {
  const t0 = performance.now();
  const res = await fetch(`/api/edge?t=${Date.now()}-${Math.random()}`, {
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new Error(`EDGE probe failed with HTTP ${res.status}`);
  const rttMs = Math.round(performance.now() - t0);
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    body = {};
  }
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  return {
    rttMs,
    colo: str(body.colo),
    http: str(body.http),
    tls: str(body.tls),
    asn: num(body.asn),
    asOrg: str(body.asOrg),
    city: str(body.city),
    country: str(body.country),
    latitude: num(body.latitude),
    longitude: num(body.longitude),
    t: str(body.t) ?? new Date().toISOString(),
  };
}

function clearProbeTimer(): void {
  if (timer) window.clearTimeout(timer);
  timer = 0;
}

function scheduleProbe(activeLifecycle: number): void {
  if (!started || lifecycle !== activeLifecycle || document.hidden) return;
  clearProbeTimer();
  timer = window.setTimeout(() => {
    timer = 0;
    void runProbe(activeLifecycle);
  }, PROBE_INTERVAL_MS);
}

async function runProbe(activeLifecycle: number): Promise<void> {
  if (
    !started ||
    lifecycle !== activeLifecycle ||
    document.hidden ||
    controller
  ) {
    return;
  }

  const request = new AbortController();
  controller = request;
  try {
    const sample = await oneProbe(request.signal);
    if (
      !started ||
      lifecycle !== activeLifecycle ||
      document.hidden ||
      controller !== request
    ) {
      return;
    }
    samples = samples.concat([sample]).slice(-MAX_LIVE);
    emit();
    document.dispatchEvent(
      new CustomEvent("doorpost:probe", { detail: { samples: samples.slice() } }),
    );
  } catch {
    /* keep last good sample */
  } finally {
    if (controller !== request) return;
    controller = null;
    scheduleProbe(activeLifecycle);
  }
}

function handleVisibilityChange(): void {
  if (!started) return;
  clearProbeTimer();
  if (document.hidden) {
    const request = controller;
    controller = null;
    request?.abort();
    return;
  }
  void runProbe(lifecycle);
}

export async function startEdgeFeed(): Promise<void> {
  if (started) return;
  started = true;
  const activeLifecycle = ++lifecycle;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  await runProbe(activeLifecycle);
}

export function stopEdgeFeed(): void {
  if (!started) return;
  started = false;
  lifecycle += 1;
  clearProbeTimer();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  const request = controller;
  controller = null;
  request?.abort();
}
